<template>
    <div>
        <div class="container-fluid mt-45">
            <div class="row">
                <div class="col-md-6">
                    <Breadcrumbs />
                </div>

                <div class="col-md-6 text-right">
                    <button @click="cancelTest()" class="btn btn-danger btn-cancel-test btn-block-mobile btn-lg lg-shadow mt-4 mt-md-0">Cancel test</button>
                </div>
            </div>

            <div class="row mt-4">
                <div class="col">
                    <div class="card">
                        <div class="card-body">
                            <h5>Please enter the patients address or postcode to find the nearest collection point</h5>

                            <div class="row mt-4">
                                <div class="col-md-4">
                                    Address / postcode <span class="text-danger">*</span>
                                    <form class="form-inline w-100" @submit.prevent="searchCollectionPoints()">
                                        <label class="sr-only">Address / postcode</label>
                                        <vue-google-autocomplete
                                            id="autocomplete"
                                            ref="autocomplete"
                                            classname="form-control"
                                            placeholder="Start typing"
                                            v-on:placechanged="getAddressData"
                                            :country="['uk']"
                                            :value="formatAddress(patientAddress)"
                                            class="w-70"
                                        >
                                        </vue-google-autocomplete>
                                        <button :disabled="loading" type="submit" class="btn btn-success btn-form sm-shadow btn-block-mobile mt-2 mt-sm-0 ml-2" :class="{disabled : loading}">Search <span v-if="loading" class="btn-loading"></span></button>
                                    </form>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <Loader v-if="loading" />

            <div class="mt-4" v-if="!loading && results.results && results.results.length">
                <p class="h4">Locations <small class="font-italic text-sm">{{ `Showing ${showingComputed} of ${results.totalCount}` }}</small></p>

                <b-table 
                    ref="table"
                    id="table"
                    :sort-by.sync="sortBySync" 
                    :sort-desc.sync="sortDesc" 
                    @sort-changed="sortingChanged" 
                    :no-local-sorting="true"
                    :busy.sync="isBusy"
                    class="custom-table bg-white" 
                    responsive
                    no-sort-reset
                    :fields="filteredCollectionPointFields" 
                    :items="results.results"
                >
                    <template #cell(type)="data">
                        <span v-if="data.item.type === CollectionPointType.PHARMACY">Pharmacy</span>
                        <span v-if="data.item.type === CollectionPointType.PLACE_OF_WORSHIP">Place of worship</span>
                        <span v-if="data.item.type === CollectionPointType.RETAIL">Retail</span>
                        <span v-if="data.item.type === CollectionPointType.COMMUNITY_CENTRE">Community centre</span>
                    </template>

                    <template #cell(address)="data">
                        {{ formatAddress(data.item) }}
                    </template>

                    <template #cell(websiteAddress)="data">
                        <a v-if="data.item.websiteAddress" target="_blank" class="underlined font-weight-bold" :href="data.item.websiteAddress">View website</a>
                        <span v-else>N/A</span>
                    </template>

                    <template #cell(action)="data">
                        <a target="_blank" :href="`https://maps.google.com/?q=${encodedAddress(data.item)}`" class="btn btn-yellow btn-view-map sm-shadow text-nowrap">View map</a>
                        <button style="width: 100px" v-if="collectionPoint != data.item.id" @click="selectCollectionPoint(data.item)" type="button" class="btn btn-primary sm-shadow text-nowrap ml-2">Select</button>
                        <button style="width: 100px" v-else type="button" class="btn btn-primary btn-collection-point-selected sm-shadow text-nowrap ml-2 disabled">Selected</button>
                    </template>
                </b-table>

                <div class="mt-4">
                    <b-pagination
                        v-model="currentPage"
                        :current-page="currentPage"
                        :per-page="results.pageSize"
                        :total-rows="results.totalCount"
                        prev-text="< Previous"
                        next-text="Next >"
                        @change="pageChange"
                        aria-controls="tests-table"
                        hide-goto-end-buttons
                        prev-class="prev-page"
                        next-class="next-page"
                        align="right"
                    ></b-pagination>
                </div>
            </div>
        </div>

        <div class="fixed-bottom bg-white py-25">
            <div class="container-fluid">
                <div class="row">
                    <div class="col text-right center-mobile">
                        <router-link :to="{name: 'Location'}" class="btn btn-cancel btn-lg px-5 lg-shadow">Back</router-link>
                        <button @click="next()" class="btn btn-success btn-lg px-6 lg-shadow ml-2">Next</button>

                        <div v-if="error">
                            <p class="text-danger text-sm mb-0 mt-2">You must select a collection point before continuing</p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import Loader from '../../../Common/components/Loader.vue'
import {mapGetters, mapActions} from 'vuex'
import axios from 'axios'
import VueGoogleAutocomplete from 'vue-google-autocomplete'
import Breadcrumbs from '@/partials/site-structure/BreadcrumbsComponent.vue'
import CancelTestModalComponent from '@/components/modals/CancelTestModalComponent.vue'
import FormatterMixin from '../../../Common/mixins/FormatterMixin'
import TotalResultsMixin from '../../../Common/mixins/TotalResultsMixin'
import CollectionPointType from '../../../Common/enums/CollectionPointType'
export default {
    name: 'CollectionPoint',

    components: {
        Loader,
        Breadcrumbs,
        VueGoogleAutocomplete
    },

    mixins: [
        FormatterMixin,
        TotalResultsMixin
    ],

    data() {
        return {
            loading: false,
            error: false,
            results: {},
            latitude: null,
            longitude: null,
            sortByValue: null,
            isBusy: false,
            currentPage: 1,
            pageSize: process.env.VUE_APP_PAGINATION_PAGE_SIZE,
            offset: 0,
            sortColumn: 'DISTANCE',
            sortType: 'ASCENDING',
            sortDesc: true,
            sortBySync: 'name',
            CancelTestModalComponent,
            CollectionPointType
        }
    },

    mounted() {
        if(!this.patientName && !this.facilityId && !this.patientId && !this.patientTitle && !this.dueDate && !this.reason && this.shippingAddressType !== 1) {
            return this.$router.push({name: 'Dashboard'})
        }

        if(this.patientAddress.latitude != null && this.patientAddress.longitude != null) {
            this.latitude = this.patientAddress.latitude
            this.longitude = this.patientAddress.longitude

            this.getCollectionPoints()
        }
    },

    computed: {
        ...mapGetters({
            patientName: 'TestStore/GetPatientName',
            patientAddress: 'TestStore/GetPatientAddress',
            facilityId: 'TestStore/GetFacilityId',
            patientId: 'TestStore/GetPatientId',
            patientTitle: 'TestStore/GetPatientTitle',
            dueDate: 'TestStore/GetDueDate',
            reason: 'TestStore/GetReason',
            deviceId: 'TestStore/GetDeviceId',
            shippingAddressType: 'TestStore/GetShippingAddressType',
            collectionPoint: 'TestStore/GetCollectionPoint'
        }),

        filteredCollectionPointFields() {
            return [
                {
                    key: 'name',
                    label: 'Name',
                    variant: 'text-wrap w-15',
                    sortable: true
                },
                {
                    key: 'type',
                    label: 'Type',
                    sortable: true
                },
                {
                    key: 'address',
                    label: 'Address',
                    sortable: false
                },
                {
                    key: 'distance',
                    label: 'Distance',
                    sortable: true,
                    formatter: 'formatDistanceInMiles'
                },
                {
                    key: 'websiteAddress',
                    label: 'Website'
                },
                {
                    key: 'action',
                    label: ''
                }
            ]
        }
    },

    methods: {
        ...mapActions({
            setCollectionPointIdInTestStoreAction: 'TestStore/SetCollectionPoint',
            setDeliveryAddressInTestStoreAction: 'TestStore/SetDeliveryAddress',
            setNotification: 'NotificationStore/SetNotification'
        }),

        getAddressData(addressData) {
            this.latitude = addressData.latitude
            this.longitude = addressData.longitude
        },

        async searchCollectionPoints() {
            await this.getCollectionPoints()
        },

        async getCollectionPoints() {
            this.isBusy = true

            try {
                let response = await axios.get(`${process.env.VUE_APP_AXIOS_ORGANISATION_BASE_URL}/api/v1/collectionpoints/${this.latitude}/${this.longitude}/${this.pageSize}/${this.offset}/${this.sortColumn}/${this.sortType}`)
            
                this.results = response.data
            } 
            catch {
                this.setNotification({
                    type: 'error',
                    title: 'Error',
                    message: 'Sorry, something went wrong while fetching the collection points'
                })
            }
            finally {
                this.loading = false
                this.isBusy = false
            }
        },

        encodedAddress(data) {
            return encodeURIComponent(this.formatAddress(data, true))
        },  

        async selectCollectionPoint(data) {
            this.error = false
            await this.setCollectionPointIdInTestStoreAction(data.id)
            await this.setDeliveryAddressInTestStoreAction(data)
        },

        next() {
            if(this.collectionPoint == null) {
                this.error = true
                return
            }

            return this.$router.push({name: 'Confirmation'})
        },

        pageChange(value) {
            this.currentPage = value
            this.offset = (value - 1) * this.pageSize
            this.getCollectionPoints()
        },

        sortingChanged(e) {
            switch(e.sortBy) {
                case 'name':
                    this.sortColumn = 'NAME' 
                break;

                case 'type':
                    this.sortColumn = 'TYPE'
                break;

                case 'distance':
                    this.sortColumn = 'DISTANCE'
                break;
            }

            this.sortDesc = e.sortDesc
            this.sortBySync = e.sortBy

            if(this.sortDesc) {
                this.sortType = 'DESCENDING'
            } else {
                this.sortType = 'ASCENDING'
            }

            this.getCollectionPoints()
        },

        cancelTest() {
            this.$modal.show(CancelTestModalComponent, {}, {width: '400px'});
        }
    }
}
</script>